import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IUnitProps } from "./Unit.interface";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { useGetModule, useGetUnit } from "../../shared/hooks/useModules";
import { IModule, IModuleUnit } from "../../shared/interfaces/modules.interface";
import { routes } from "../../shared/router/routes";
import ComponentPicker from "../../components/UnitComponents/ComponentPicker/ComponentPicker";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL, DEFAULT_LANGUAGE } from "../../shared/constants";
import UnitCard from "../../components/UnitCard/UnitCard";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import { UserContext } from "../../shared/providers/user/user.provider";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import Link from "../../components/Link/Link";

function Unit(props: IUnitProps) {
    const navigate = useNavigate();
    const { lang, translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { modules, module, setModule, setCurrentUnitId, setUpdated } = useContext(ModuleContext);

    const { unitId: id, moduleId } = useParams();
    const { isLoading, data: unit } = useGetUnit<IModuleUnit>(id);
    const { data: moduleData } = useGetModule<IModule>(moduleId, lang ?? DEFAULT_LANGUAGE);
    const [nextModule, setNextModule] = useState<IModule | undefined>();

    const nextIndex = useMemo(
        () => (module?.units?.findIndex((unit) => unit.id === id) ?? 0) + 1,
        [module, id]
    );
    const previousIndex = useMemo(
        () => (module?.units?.findIndex((unit) => unit.id === id) ?? 0) - 1,
        [module, id]
    );
    const hasNextUnit = useMemo(() => module && module.units?.[nextIndex], [nextIndex, module]);
    const hasPreviousUnit = useMemo(
        () => module && module.units?.[previousIndex],
        [previousIndex, module]
    );

    useEffect(() => {
        if (!modules || !module) return;

        const currentModuleIndex = modules.findIndex((mod) => mod.id === module.id);
        const nextModule = modules[currentModuleIndex + 1];
        if (nextModule) setNextModule(nextModule);
    }, [module, modules]);

    useEffect(() => {
        setIsLoading(isLoading || !module);

        if (unit || isLoading) return;

        if (isLoading) return;
        if (!unit) navigate(routes.MODULES.path);
    }, [isLoading, module, navigate, unit]);

    useEffect(() => {
        if (module) return;

        setModule(moduleData);
    }, [module, moduleData]);

    useEffect(() => {
        setCurrentUnitId(id);
    }, [id]);

    useEffect(() => {
        return () => setUpdated(new Date());
    }, []);

    return (
        <>
            <div className="page_unit row pb-md-6 px-1 px-md-2 px-lg-0">
                <div className="unit_nav col-12 col-lg-3 d-flex align-items-start ps-md-21 mb-12">
                    <Link
                        to={".."}
                        icon="Chevron--left"
                        iconSize={16}
                        customCss="ms-0 ms-lg-2 ms-n1 ms-md-0 py-md-0 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
                        {translate("modules.table_of_contents", "Table of contents")}
                    </Link>
                </div>
                <div className="col-12 col-lg-6">
                    <div className="unit d-flex flex-column h-100">
                        <div className="d-flex flex-column gap-1 mb-1">
                            <div className="row gx-0">
                                <div className="col-12 d-flex flex-column gap-02">
                                    <span className="text-bd3-rg color-neutrals-dark">
                                        Unit {nextIndex}
                                    </span>
                                    <h1 className="text-h2-smbd color-neutrals-black mb-02">
                                        {unit?.title}
                                    </h1>
                                    <p className="text-h3-rg color-neutrals-dark m-0">
                                        {unit?.description}
                                    </p>
                                </div>
                            </div>
                            {unit?.items.map((item, i) => (
                                <ComponentPicker key={i} unitId={id} {...item}></ComponentPicker>
                            ))}
                        </div>
                        <div className="row g-0 g-md-12 mt-md-6">
                            {hasPreviousUnit && (
                                <div className={`${hasNextUnit ? "col-lg-6" : "" } col-12`}>
                                    <div className="unit_next bg-blue-50 px-11 pt-22 pt-md-2 pb-12 mx-n1 mx-md-0 h-100">
                                        <div className="row gx-0">
                                            <div className="col-12">
                                                <h2 className="text-h4-smbd mb-12">
                                                    {translate(
                                                        "modules.previous_module_unit",
                                                        "Previous module unit"
                                                    )}
                                                </h2>
                                                <UnitCard
                                                    moduleId={module!.id}
                                                    moduleImage={
                                                        module!.image?.url
                                                            ? `${CONTENT_BASE_URL}${module!.image?.url}`
                                                            : Placeholder
                                                    }
                                                    unit={module!.units?.[previousIndex] as IModuleUnit}
                                                    index={previousIndex + 1}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {hasNextUnit && (
                                <div className={`${hasPreviousUnit ? "col-lg-6" : "" } col-12`}>
                                    <div className="unit_next bg-blue-50 px-11 pt-22 pt-md-2 pb-12 mx-n1 mx-md-0 h-100">
                                        <div className="row gx-0">
                                            <div className="col-12">
                                                <h2 className="text-h4-smbd mb-12">
                                                    {translate(
                                                        "modules.next_module_unit",
                                                        "Next module unit"
                                                    )}
                                                </h2>
                                                <UnitCard
                                                    moduleId={module!.id}
                                                    moduleImage={
                                                        module!.image?.url
                                                            ? `${CONTENT_BASE_URL}${module!.image?.url}`
                                                            : Placeholder
                                                    }
                                                    unit={module!.units?.[nextIndex] as IModuleUnit}
                                                    index={nextIndex + 1}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {!hasNextUnit && (
                            <div className="unit_action row pb-1 mx-n1 mx-md-0 px-1 px-md-0 pt-md-2">
                                <div className="col-12 col-md-6">
                                    <Link
                                        variant="solid"
                                        customCss="w-100"
                                        to={routes.MODULES.path}>
                                        {translate("modules.conclude_module", "Conclude module")}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Unit;
